/**
 * Placement of annotation titles and ruler labels, plus their text color.
 * All rects are canvas CSS pixels. `image` is the whole image in canvas
 * pixels (not only the visible part), so labels stay attached to their box
 * while panning and zooming and flip inward only at the real image edge.
 */
import type { ImageRect } from './image-canvas-viewport';

export const TITLE_HEIGHT = 20;
export const LABEL_FONT = '12px sans-serif';
export const TITLE_FONT = `bold ${LABEL_FONT}`;
export const LABEL_PADDING = 8;
export const DARK_TEXT_COLOR = '#0f1117';
export const LIGHT_TEXT_COLOR = '#ffffff';
/** Annotation box stroke. It is centered on the path, so half of it lies outside the box. */
export const BOX_STROKE_WIDTH = 2;

type Rgb = [number, number, number];

export function titleRect(
    box: ImageRect,
    textWidth: number,
    below: boolean,
    image: ImageRect
): ImageRect {
    const outset = BOX_STROKE_WIDTH / 2;
    const outerLeft = Math.max(box.x - outset, image.x);
    const outerRight = Math.min(
        box.x + box.width + outset,
        image.x + image.width
    );
    const outerWidth = Math.max(0, outerRight - outerLeft);
    const width = Math.min(
        Math.max(outerWidth, textWidth + LABEL_PADDING),
        image.width
    );
    const height = Math.min(TITLE_HEIGHT, image.height);
    const x = alignStart(outerLeft, outerWidth, width, image.x, image.width);
    const y = below
        ? placeAfter(box.y, box.height, height, image.y, image.height)
        : placeBefore(box.y, height, image.y, image.height);
    return { x, y, width, height };
}

/** Width ruler: centered below the box, inside it at the bottom image edge. */
export function widthLabelRect(
    box: ImageRect,
    textWidth: number,
    image: ImageRect
): ImageRect {
    const width = Math.min(textWidth + LABEL_PADDING, image.width);
    const height = Math.min(TITLE_HEIGHT, image.height);
    return {
        x: center(box.x, box.width, width, image.x, image.width),
        y: placeAfter(box.y, box.height, height, image.y, image.height),
        width,
        height,
    };
}

/** Height ruler: centered left of the box, inside it at the left image edge. */
export function heightLabelRect(
    box: ImageRect,
    textWidth: number,
    image: ImageRect
): ImageRect {
    const width = Math.min(TITLE_HEIGHT, image.width);
    const height = Math.min(textWidth + LABEL_PADDING, image.height);
    return {
        x: placeBefore(box.x, width, image.x, image.width),
        y: center(box.y, box.height, height, image.y, image.height),
        width,
        height,
    };
}

/** Light or dark text, whichever contrasts more with the background. */
export function labelTextColor(background: string): string {
    const rgb = parseRgb(background);
    if (!rgb) {
        return DARK_TEXT_COLOR;
    }
    const light = parseRgb(LIGHT_TEXT_COLOR) as Rgb;
    const dark = parseRgb(DARK_TEXT_COLOR) as Rgb;
    return contrastRatio(rgb, light) > contrastRatio(rgb, dark)
        ? LIGHT_TEXT_COLOR
        : DARK_TEXT_COLOR;
}

function placeBefore(
    start: number,
    size: number,
    imageStart: number,
    imageLength: number
): number {
    const outside = start - size;
    return clampWithin(
        outside < imageStart ? start : outside,
        size,
        imageStart,
        imageLength
    );
}

function placeAfter(
    start: number,
    length: number,
    size: number,
    imageStart: number,
    imageLength: number
): number {
    const end = start + length;
    return clampWithin(
        end + size > imageStart + imageLength ? end - size : end,
        size,
        imageStart,
        imageLength
    );
}

function alignStart(
    start: number,
    length: number,
    size: number,
    imageStart: number,
    imageLength: number
): number {
    return clampWithin(
        start + size > imageStart + imageLength ? start + length - size : start,
        size,
        imageStart,
        imageLength
    );
}

function center(
    start: number,
    length: number,
    size: number,
    imageStart: number,
    imageLength: number
): number {
    return clampWithin(
        start + length / 2 - size / 2,
        size,
        imageStart,
        imageLength
    );
}

function clampWithin(
    value: number,
    size: number,
    start: number,
    length: number
): number {
    return Math.max(
        start,
        Math.min(Math.max(start, start + length - size), value)
    );
}

function parseRgb(color: string): Rgb | null {
    const hex = color.match(/^#([\da-f]{3}|[\da-f]{6})$/i)?.[1];
    if (hex) {
        const full =
            hex.length === 3
                ? hex
                      .split('')
                      .map((digit) => digit + digit)
                      .join('')
                : hex;
        return [0, 2, 4].map((offset) =>
            parseInt(full.slice(offset, offset + 2), 16)
        ) as Rgb;
    }
    const rgb = color.match(
        /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\s*\)$/i
    );
    return rgb ? [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])] : null;
}

function relativeLuminance(rgb: Rgb): number {
    const [red, green, blue] = rgb.map((component) => {
        const normalized = component / 255;
        return normalized <= 0.03928
            ? normalized / 12.92
            : ((normalized + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function contrastRatio(first: Rgb, second: Rgb): number {
    const a = relativeLuminance(first);
    const b = relativeLuminance(second);
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}
